import * as React from 'react';
import { cn } from '../lib/cn';
import { Button } from '../primitives/Button';
import { CheckIcon } from '../icons/CheckIcon';

export interface PricingCardProps {
  name: string;
  price: string;
  period?: string;
  description?: string;
  features: string[];
  featured?: boolean;
  ctaText?: string;
  ctaHref?: string;
  onCtaClick?: () => void;
  className?: string;
}

const PricingCard = React.forwardRef<HTMLDivElement, PricingCardProps>(
  (
    {
      name,
      price,
      period = 'month',
      description,
      features,
      featured = false,
      ctaText = 'Get Started',
      ctaHref,
      onCtaClick,
      className,
    },
    ref
  ) => (
    <div
      ref={ref}
      className={cn(
        'relative flex flex-col rounded border-2 bg-card text-card-foreground p-6',
        'transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-card-hover',
        featured ? 'border-warning shadow-md' : 'border-border shadow-card',
        className
      )}
    >
      {featured && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="rounded-sm bg-primary px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary-foreground">
            Most Popular
          </span>
        </div>
      )}

      <div className={cn('text-center', featured && 'pt-2')}>
        <h3 className="font-display text-xl font-semibold text-foreground">{name}</h3>
        {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
      </div>

      <div className="mt-4 text-center">
        <span className="font-display text-4xl font-bold tabular-nums text-foreground">R{price}</span>
        <span className="text-muted-foreground">/{period}</span>
      </div>

      <ul className="mt-6 flex-1 space-y-3">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start gap-3">
            <CheckIcon className="mt-0.5 h-5 w-5 shrink-0 text-success" />
            <span className="text-sm text-muted-foreground">{feature}</span>
          </li>
        ))}
      </ul>

      <div className="mt-6">
        {ctaHref ? (
          <a href={ctaHref} className="block">
            <Button
              variant={featured ? 'orange' : 'secondary'}
              size="lg"
              className="w-full"
            >
              {ctaText}
            </Button>
          </a>
        ) : (
          <Button
            variant={featured ? 'orange' : 'secondary'}
            size="lg"
            className="w-full"
            onClick={onCtaClick}
          >
            {ctaText}
          </Button>
        )}
      </div>
    </div>
  )
);
PricingCard.displayName = 'PricingCard';

export { PricingCard };
